/**
 * `PUT /me/sessions/:id/soreness` — the Today check-in — as a mutation that
 * survives the app being closed offline (T41). The same shape as
 * `features/workout/after/feedback-mutation.ts`, for the same reason.
 *
 * ── WHY IT MUST BE A REGISTERED DEFAULT ────────────────────────────────────
 * Offline, TanStack pauses the mutation and the persister stores its key and
 * variables — never its function. On the next launch the cache is restored
 * and TanStack looks the function up by `mutationKey` in
 * `setMutationDefaults` AT RESTORE TIME. A default registered later (this
 * module is loaded with the Today screen, after the restore) is too late, and
 * the restored mutation can never run: the card asks the question again and
 * the first answer is lost. `registerSorenessMutationDefaults` is therefore
 * called from `data/mutations.ts`, which `provider.tsx` runs before the
 * persister restores.
 *
 * ── PUT REPLACES, SO A REPLAY IS HARMLESS ──────────────────────────────────
 * One soreness answer per session, last write wins. Retrying after an unseen
 * success writes the same row again.
 *
 * ── IT IS ONLY AS DURABLE AS THE CACHE'S SHAPE ─────────────────────────────
 * The persisted cache is busted when a shape it holds can have changed
 * (`buster`, `persist.ts`, `D190`), so a paused check-in does NOT survive an
 * update that changes one: the cache it sits in is thrown away,
 * `GET /me/pending` still says pending, and the card asks once more. An
 * update that changes no shape keeps it, and the PUT below replays as it
 * would have. That is the accepted T17 trade-off for a write
 * that is not in the outbox — the outbox is for the workout's own writes,
 * which must replay in order, and a lost check-in costs one extra tap, never
 * a lost set.
 *
 * ── NOT ORDERED BEHIND ANYTHING ────────────────────────────────────────────
 * Unlike the feedback, the check-in is only ever offered for a session the
 * server itself named (`GET /me/pending`, or an insight's `open_checkin`), so
 * its start has already landed. A 404 here is somebody else's session and is
 * not worth retrying.
 */

import { ApiError, SessionExpiredError } from "@repo/client/api";
import type { AuthorizedFetcher } from "@repo/client/data/core";
import { sorenessMutation } from "@repo/client/data/workout";
import type { SorenessInput } from "@repo/contract/training";
import type { QueryClient } from "@tanstack/react-query";
import { invalidateKeys } from "../../data/query-client";

export type SorenessVariables = SorenessInput & {
  readonly sessionId: string;
};

/** T19's key (`sorenessMutation`), so the registry and the hook agree. */
export const SORENESS_MUTATION_KEY = [
  "workout",
  "sessions",
  "soreness",
] as const;

/** Enough to outlast a server having a bad few minutes. */
export const SORENESS_MAX_RETRIES = 8;

const MAX_DELAY_MS = 60_000;

/**
 * Retry what time can fix: the network, a 5xx and a 429. A 4xx that is the
 * request's fault (a 422 from a body this build got wrong, a 404 for a
 * session that is not ours) gets the same answer every time and stops at
 * once. An expired session never retries — the sign-in flow owns that.
 */
export function shouldRetrySoreness(
  failureCount: number,
  error: unknown
): boolean {
  if (failureCount >= SORENESS_MAX_RETRIES) {
    return false;
  }
  if (error instanceof SessionExpiredError) {
    return false;
  }
  if (error instanceof ApiError) {
    return error.status >= 500 || error.status === 429;
  }
  return true;
}

/** 1 s, 2 s, 4 s … capped at a minute. */
export function sorenessRetryDelay(failureCount: number): number {
  return Math.min(MAX_DELAY_MS, 1000 * 2 ** Math.max(0, failureCount - 1));
}

/**
 * `networkMode: "online"` is what makes the write PAUSE offline rather than
 * fail: the card reads that paused state to show the answer back (T41), and
 * the pause is what the persister stores.
 */
export function sorenessMutationDefaults(
  api: AuthorizedFetcher,
  client: Pick<QueryClient, "invalidateQueries">
) {
  const def = sorenessMutation(api);
  return {
    mutationFn: (variables: SorenessVariables) => def.mutationFn(variables),
    networkMode: "online" as const,
    onError: (error: unknown) => {
      console.error("[today] the check-in was not delivered", error);
    },
    // The check-in card, the nav badge and the pain insight read these.
    onSuccess: () => invalidateKeys(client, def.invalidates ?? []),
    retry: shouldRetrySoreness,
    retryDelay: sorenessRetryDelay,
  };
}

/** For `data/mutations.ts` (see the header): one line there. */
export function registerSorenessMutationDefaults(
  client: Pick<QueryClient, "invalidateQueries" | "setMutationDefaults">,
  api: AuthorizedFetcher
): void {
  client.setMutationDefaults(
    [...SORENESS_MUTATION_KEY],
    sorenessMutationDefaults(api, client)
  );
}
